/**
 * The space's timezone, for drawing a time where it HAPPENS rather than on the reader's clock.
 *
 * `spaces.config.timezone` (Settings → Calendar timezone) is set once as a space loads
 * (WorkspaceRouter). Null means the space never chose one, and then every time stays on the reader's
 * own clock exactly as before — nothing here changes a space that has not opted in.
 *
 * A time drawn in the space's zone carries a short zone name ("PT") only when the reader's clock
 * reads differently at THAT instant: someone in Vancouver looking at a Los Angeles event needs no
 * label, and neither does anyone in a zone that happens to share the offset that day.
 *
 * Pure apart from the module-level zone, so `node --test` loads it.
 */

let spaceZone = null;

function validZone(zone) {
  if (!zone || typeof zone !== 'string') return null;
  try { new Intl.DateTimeFormat('en-US', { timeZone: zone }); return zone; } catch { return null; }
}

/** Set the space's zone; null or an unknown name clears it. Returns the zone in use (or null). */
export function setSpaceTimeZone(zone) {
  spaceZone = validZone(zone);
  return spaceZone;
}

/** The space's zone, or null when it has none. */
export function spaceTimeZone() { return spaceZone; }

/** The reader's own zone. */
export function viewerTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

/** Minutes east of UTC for `zone` at instant `d`. */
function offsetAt(d, zone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
  }).formatToParts(d);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  return Math.round((asUtc - Math.floor(d.getTime() / 60000) * 60000) / 60000);
}

/**
 * The short name of `zone` ("PT", "ET") when the viewer's clock reads differently at `d`, else ''.
 * `viewer` is injectable for tests.
 */
export function zoneLabelAt(d, zone, viewer = viewerTimeZone()) {
  if (!zone || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  if (viewer && (viewer === zone || offsetAt(d, viewer) === offsetAt(d, zone))) return '';
  for (const style of ['shortGeneric', 'short']) {
    try {
      const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style })
        .formatToParts(d).find((p) => p.type === 'timeZoneName')?.value;
      if (name) return name;
    } catch { /* an engine without shortGeneric falls back to short */ }
  }
  return '';
}

/**
 * "Oct 22, 2026, 3:00 PM" in `zone`, with " PT" when the viewer's clock differs. Date-only when
 * `withTime` is false (the day is still the one in `zone`).
 */
export function formatInZone(d, zone, { withTime = true, viewer } = {}) {
  const opts = { timeZone: zone, month: 'short', day: 'numeric', year: 'numeric' };
  if (withTime) Object.assign(opts, { hour: 'numeric', minute: '2-digit' });
  const text = new Intl.DateTimeFormat('en-US', opts).format(d);
  const label = withTime ? zoneLabelAt(d, zone, viewer === undefined ? viewerTimeZone() : viewer) : '';
  return label ? `${text} ${label}` : text;
}

/**
 * The wall clock `zone` shows at instant `d`: { year, month (1-12), day, hour, minute }. Null for
 * an invalid date. This is what an editor fills its date and time boxes with, and the day a
 * calendar files the event under, once the space has a zone.
 */
export function wallInZone(d, zone) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime()) || !zone) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
  }).formatToParts(d);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute') };
}

/**
 * The instant at which `zone`'s wall clock reads `wall` — the inverse of wallInZone. The offset is
 * read twice because the first guess can sit on the other side of a clock change; a wall time the
 * change skips (2:30 AM on the spring-forward night) lands an hour later, as a clock would show it.
 */
export function instantFromWall({ year, month, day, hour = 0, minute = 0 }, zone) {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(guess) || !zone) return null;
  const first = guess - offsetAt(new Date(guess), zone) * 60000;
  const second = guess - offsetAt(new Date(first), zone) * 60000;
  return new Date(second);
}
